import { logger } from '../../services/logger';
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db';
import { toast } from 'react-hot-toast';
import { Loader2, Search, ArrowUpDown, Calendar, DollarSign, Filter, RefreshCw, Percent, TrendingUp } from 'lucide-react';
import { useSettings } from '../../lib/settings/settingsContext';
import { ClickableUserName } from '../common/ClickableUserName';

// Define expected props
interface BetViewerProps {
  marketId?: string;
  // bets?: UserBet[]; // Example bet data prop
  // onPayout?: (betIds: string[]) => void; // Example payout handler
  // onRefund?: (betIds: string[]) => void; // Example refund handler
}

interface Bet {
  id: string;
  user_id: string;
  market_id: string;
  market_option_id: number;
  amount: number;
  amount_after_fee: number;
  odds: number;
  status: 'pending' | 'won' | 'lost' | 'cancelled';
  time: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  market_option_name: string;
  potential_payout: number;
}

// TODO: Fetch and display bets, implement filtering, sorting, and actions
// Props will likely include marketId, market status, and potentially bet data

export function BetViewer({ marketId }: BetViewerProps) {
  const { formatCurrency } = useSettings();

  const [bets, setBets] = useState<Bet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Bet | 'potential_payout'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<Bet['status'] | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  const [amountRange, setAmountRange] = useState<{ min: string; max: string }>({
    min: '',
    max: ''
  });
  const [oddsRange, setOddsRange] = useState<{ min: string; max: string }>({
    min: '',
    max: ''
  });
  const [potentialPayoutRange, setPotentialPayoutRange] = useState<{ min: string; max: string }>({
    min: '',
    max: ''
  });
  
  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  // Fetch bets for the market
  useEffect(() => {
    const fetchBets = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params: {
          p_market_id: string | undefined;
          p_sort_field: keyof Bet | 'potential_payout';
          p_sort_direction: 'asc' | 'desc';
          p_status_filter: Bet['status'] | null;
          p_search_term: string | null;
          p_date_start_filter?: string;
          p_date_end_filter?: string;
          p_amount_min_filter?: number;
          p_amount_max_filter?: number;
          p_odds_min_filter?: number;
          p_odds_max_filter?: number;
          p_potential_payout_min_filter?: number;
          p_potential_payout_max_filter?: number;
        } = {
          p_market_id: marketId,
          p_sort_field: sortField as keyof Bet,
          p_sort_direction: sortDirection,
          p_status_filter: statusFilter === 'all' ? null : statusFilter,
          p_search_term: debouncedSearchTerm === '' ? null : debouncedSearchTerm,
        };

        if (dateRange.start) params.p_date_start_filter = dateRange.start;
        if (dateRange.end) params.p_date_end_filter = dateRange.end;
        if (amountRange.min) params.p_amount_min_filter = parseFloat(amountRange.min);
        if (amountRange.max) params.p_amount_max_filter = parseFloat(amountRange.max);
        if (oddsRange.min) params.p_odds_min_filter = parseFloat(oddsRange.min);
        if (oddsRange.max) params.p_odds_max_filter = parseFloat(oddsRange.max);
        if (potentialPayoutRange.min) params.p_potential_payout_min_filter = parseFloat(potentialPayoutRange.min);
        if (potentialPayoutRange.max) params.p_potential_payout_max_filter = parseFloat(potentialPayoutRange.max);

        // logger.info('Calling get_bets_for_market_admin with params:', params); // Removing this line

        // Call the database function
        const { data: fetchedBets, error: rpcError } = await supabase
          .rpc('get_bets_for_market_admin', params);

        if (rpcError) throw rpcError;

        // The data from RPC is already in the desired shape
        // We need to map it slightly if the RPC returns fields that need to be nested for the old interface,
        // or update the Bet interface and usage to match the flat structure from RPC.
        // For this example, I'll assume the Bet interface is updated to match the flat structure.
        
        // Map the flat structure from RPC to the expected Bet structure if needed,
        // or ensure Bet interface matches the flat structure.
        // The SQL function is designed to return flat columns like first_name, last_name, email, market_option_name
        // const transformedBets = (fetchedBets || []).map((bet: Bet) => ({
        //   ...bet,
        //   // Ensure raw_user_meta_data and market_option are structured as expected by the rest of the component
        //   // if the component hasn't been fully updated to use the flat structure.
        //   // Based on the updated Bet interface, this mapping might not be needed if the rest of the component
        //   // adapts to first_name, last_name, email, market_option_name directly on the bet object.
        //   // For now, assuming direct usage:
        // }));


        setBets(fetchedBets || []);
      } catch (err) {
        logger.error('Error fetching bets:', err);
        setError('Failed to load trades');
        toast.error('Failed to load trades');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBets();
  }, [
    marketId, 
    sortField, 
    sortDirection, 
    debouncedSearchTerm, 
    statusFilter, 
    dateRange, 
    amountRange, 
    oddsRange,
    potentialPayoutRange
  ]);

  // Calculate potential payout for a bet
  // const calculatePotentialPayout = (bet: Bet) => { // No longer needed if potential_payout comes from backend
  //   return bet.amount * bet.odds;
  // };

  // Filter and sort bets
  const filteredBets = bets;

  // If all filtering is server-side, filteredBets can be simplified:
  // const filteredBets = bets; 
  // Or, if potentialPayoutRange is also moved to server or removed:
  // No filtering needed here if all done by RPC.

  // Handle sort
  const handleSort = (field: keyof Bet | 'potential_payout') => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (status: Bet['status']) => {
    switch (status) {
      case 'won': return 'text-green-600';
      case 'lost': return 'text-red-600';
      case 'cancelled': return 'text-gray-600';
      default: return 'text-yellow-600';
    }
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setDateRange({ start: '', end: '' });
    setAmountRange({ min: '', max: '' });
    setOddsRange({ min: '', max: '' });
    setPotentialPayoutRange({ min: '', max: '' });
    setShowFilters(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4 bg-white rounded-lg shadow p-6">
      {/* Search and Filter Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or outcome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-white"
          />
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 flex items-center gap-1 text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Bet['status'] | 'all')}
            className="px-3 py-2 text-gray-700 border border-gray-300 rounded-md bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">From</label>
                  <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="date" 
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">To</label>
                  <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="date" 
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Amount Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount Range</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Min</label>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="0"
                      value={amountRange.min}
                      onChange={(e) => setAmountRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Max</label>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="∞"
                      value={amountRange.max}
                      onChange={(e) => setAmountRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Odds Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Odds Range</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Min</label>
                  <div className="relative">
                    <Percent className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="0"
                      value={oddsRange.min}
                      onChange={(e) => setOddsRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Max</label>
                  <div className="relative">
                    <Percent className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="∞"
                      value={oddsRange.max}
                      onChange={(e) => setOddsRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Potential Payout Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Potential Payout Range</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Min</label>
                  <div className="relative">
                    <TrendingUp className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="0"
                      value={potentialPayoutRange.min}
                      onChange={(e) => setPotentialPayoutRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Max</label>
                  <div className="relative">
                    <TrendingUp className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      type="number" 
                      placeholder="∞"
                      value={potentialPayoutRange.max}
                      onChange={(e) => setPotentialPayoutRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-md bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleResetFilters}
              className="px-3 py-1 text-sm text-indigo-600 border border-indigo-200 rounded-md bg-indigo-50 hover:bg-indigo-100"
            >
              <RefreshCw className="w-3 h-3 inline-block mr-1" />
              Reset Filters
            </button>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-gray-500 mb-2">
        Showing {filteredBets.length} bet{filteredBets.length !== 1 ? 's' : ''}
      </div>

      {/* Bets Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('created_at')} className="flex items-center">
                  Date/Time
                  {sortField === 'created_at' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User (Name/Email)
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Outcome
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('amount')} className="flex items-center">
                  Amount
                  {sortField === 'amount' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('odds')} className="flex items-center">
                  Odds
                  {sortField === 'odds' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Potential Payout
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('status')} className="flex items-center">
                  Status
                  {sortField === 'status' && <ArrowUpDown className="ml-1 h-3 w-3" />}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredBets.map((bet) => {
              const fullName = `${bet.first_name} ${bet.last_name}`;
              
              return (
                <tr key={bet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(bet.time || bet.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="font-medium">
                        <ClickableUserName userId={bet.user_id} displayName={fullName} />
                      </div>
                      <div className="text-gray-500">{bet.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {bet.market_option_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(bet.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {bet.odds.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(bet.potential_payout)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`capitalize ${getStatusColor(bet.status)}`}>
                      {bet.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
} 